import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const POLL_MS = 60_000; // 1 min

/** Watches /api/version. When the server version changes AFTER the app was
 * loaded, users see a "New version available" toast with a Refresh button.
 * Combined with the network-first SW shell, no manual cache clear is needed.
 */
export default function VersionWatcher() {
  const bootVersion = useRef(null);
  const notified = useRef(false);

  useEffect(() => {
    let stopped = false;
    const cadence = document.hidden ? 0 : POLL_MS;

    async function poll() {
      if (stopped) return;
      try {
        const r = await api.get("/version");
        const v = r.data?.version;
        if (v && !bootVersion.current) bootVersion.current = v;
        else if (v && v !== bootVersion.current && !notified.current) {
          notified.current = true;
          toast("A new version of MedLearn is available.", {
            description: "Reload to pick up the latest fixes.",
            duration: Infinity,
            action: {
              label: "Reload now",
              onClick: async () => {
                // Ask the SW to activate immediately, then hard-reload.
                try {
                  if ("serviceWorker" in navigator) {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    regs.forEach((r) => r.waiting?.postMessage("SKIP_WAITING"));
                  }
                } catch { /* ignore */ }
                window.location.reload();
              },
            },
          });
        }
      } catch { /* ignore transient errors */ }
    }

    poll();
    const id = setInterval(poll, POLL_MS);
    // Also poll when tab regains focus.
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => {
      stopped = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
